export interface PipelineCapacityMetadata {
  capacity_basis?: 'unavailable' | 'diameter_model' | 'permit_rated' | 'operator_announced';
  capacity_scope?: 'segment_screen' | 'permit' | 'project' | null;
  source_capacity_mmcf_day?: number | null;
  source_capacity_scope?: 'system' | 'project' | 'permit' | 'segment_screen' | 'unknown';
}

interface PipelineCapacityInput extends PipelineCapacityMetadata {
  capacity_mmcf_day?: number | string | null;
  mw_potential?: number | string | null;
  diameter_inches?: number | string | null;
  capacity_source?: string | null;
  source?: string | null;
}

function nonnegativeNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function pipelineCapacityDisplay(p: PipelineCapacityInput) {
  const diameter = nonnegativeNumber(p.diameter_inches);
  const statedBasis = p.capacity_source === 'operator_announced' || p.source === 'ET press release + physical map'
    ? 'operator_announced'
    : p.source === 'WV DEP TAGIS' ? 'permit_rated' : null;
  let basis = p.capacity_basis ?? statedBasis ?? (diameter != null && diameter > 0 ? 'diameter_model' : 'unavailable');
  if (basis === 'diameter_model' && !(diameter != null && diameter > 0)) basis = 'unavailable';
  // Diameter is a physical fact, not a capacity observation. A diameter curve
  // may remain in the payload for legacy analysis, but it is never a displayed
  // capacity claim. Only explicitly scoped published values survive here.
  const capacityMmcf = basis === 'permit_rated' || basis === 'operator_announced'
    ? nonnegativeNumber(p.capacity_mmcf_day)
    : null;
  if (capacityMmcf == null) basis = 'unavailable';
  const label = basis === 'diameter_model' ? 'Diameter recorded; capacity unavailable'
    : basis === 'permit_rated' ? 'Permit-Rated Capacity'
    : basis === 'operator_announced' ? 'Operator-Announced Capacity' : 'Segment capacity unavailable';
  const explanation = basis === 'diameter_model'
    ? 'Diameter is recorded, but no sourced capacity is available for this segment.'
    : basis === 'permit_rated' ? 'Stated capacity for the documented permit; confirm operating conditions with the operator.'
    : basis === 'operator_announced' ? 'Announced project capacity; current status and in-service date are unverified.'
    : p.source === 'EIA' ? 'A system rating and generalized route do not establish capacity at this segment.'
    : 'No sourced segment capacity is available; confirm the rating and service with the operator.';
  const sourceLabel = p.source_capacity_scope === 'system' ? 'System source capacity'
    : p.source_capacity_scope === 'project' ? 'Project source capacity'
    : p.source_capacity_scope === 'permit' ? 'Permit source capacity' : 'Source capacity';
  return {
    basis,
    capacityMmcf,
    // Keep this property for existing callers while making conversion
    // impossible at the display boundary.
    powerMw: null,
    label,
    explanation,
    sourceLabel,
    sourceCapacityMmcf: ['system', 'project', 'permit'].includes(p.source_capacity_scope ?? '')
      ? nonnegativeNumber(p.source_capacity_mmcf_day)
      : null,
    sourceCapacityScope: ['system', 'project', 'permit'].includes(p.source_capacity_scope ?? '')
      ? p.source_capacity_scope
      : null,
  };
}

interface FeaturedCapacityInput extends PipelineCapacityInput {
  headline_override?: boolean;
  co_headline?: boolean;
  mw_potential_available?: number | string | null;
  local_utilization_pct?: number | string | null;
  utilization_pct?: number | string | null;
}

export function featuredPipelinePowerMw(
  parcel: { pipeline_available_mw?: number | string | null; pipeline_available_local_source?: string | null },
  featured: FeaturedCapacityInput | null,
): number | null {
  // Stored MW/headroom fields are derived claims. They do not establish
  // deliverable gas at the selected parcel, even when a permit supplied the
  // design load. Keep the helper for callers, but make its uncertainty explicit.
  void parcel;
  void featured;
  return null;
}
